import React, { memo, useState, useCallback } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Tag, Tooltip } from 'antd';
import { KeyOutlined, LinkOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';

const TABLE_TYPE_COLORS = {
  hub: '#722ed1',   // violet
  sat: '#1677ff',   // blue
  link: '#52c41a',  // green
};

const HEADER_HEIGHT = 38;
const COL_HEIGHT = 26;

const ERDTableNode = memo(({ data }) => {
  const { label, schema, columns = [], highlighted, onToggleExpand, expanded, tableType = 'hub', accessDenied = false } = data;
  const headerColor = accessDenied ? '#bfbfbf' : (highlighted ? '#1677ff' : (TABLE_TYPE_COLORS[tableType] || '#722ed1'));
  const isExpanded = expanded === true;

  const keyColumns = columns.filter(col => col.isFK || col.isPK);
  const nonKeyColumns = columns.filter(col => !col.isFK && !col.isPK);
  const displayColumns = isExpanded ? columns : keyColumns;
  const hiddenCount = isExpanded ? 0 : nonKeyColumns.length;

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    if (onToggleExpand) onToggleExpand(data.tableId || label);
  }, [onToggleExpand, data.tableId, label]);

  return (
    <div
      style={{
        background: 'var(--theme-cardBg, #fff)',
        border: `2px solid ${accessDenied ? '#bfbfbf' : (highlighted ? '#1677ff' : 'var(--theme-border, #d9d9d9)')}`,
        borderRadius: 8,
        minWidth: 220,
        maxWidth: 320,
        boxShadow: highlighted
          ? '0 0 12px rgba(22,119,255,0.3)'
          : '0 2px 8px rgba(0,0,0,0.1)',
        fontSize: 12,
        overflow: 'hidden',
        opacity: accessDenied ? 0.55 : 1,
        filter: accessDenied ? 'grayscale(0.6)' : 'none',
      }}
      title={accessDenied ? 'Access denied by administrator' : undefined}
    >
      {/* Per-column handles for precise edge routing */}
      {displayColumns.map((col, idx) => {
        const top = HEADER_HEIGHT + idx * COL_HEIGHT + COL_HEIGHT / 2;
        return (
          <React.Fragment key={`handle-${col.name}`}>
            <Handle
              type="target"
              position={Position.Left}
              id={`col-${col.name}`}
              style={{ background: col.isFK ? '#faad14' : 'transparent', width: 6, height: 6, top, border: col.isFK ? undefined : 'none' }}
            />
            <Handle
              type="source"
              position={Position.Right}
              id={`col-${col.name}`}
              style={{ background: col.isFK ? '#faad14' : 'transparent', width: 6, height: 6, top, border: col.isFK ? undefined : 'none' }}
            />
          </React.Fragment>
        );
      })}
      {/* Default fallback handles */}
      <Handle type="target" position={Position.Left} id="default" style={{ background: headerColor, width: 8, height: 8 }} />
      <Handle type="source" position={Position.Right} id="default" style={{ background: headerColor, width: 8, height: 8 }} />

      {/* Table header */}
      <div
        style={{
          background: headerColor,
          color: '#fff',
          padding: '6px 12px',
          fontWeight: 700,
          fontSize: 12,
        }}
      >
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
          {accessDenied && (
            <Tag color="red" style={{ marginLeft: 6, fontSize: 9, lineHeight: '14px', padding: '0 4px' }}>
              No access
            </Tag>
          )}
        </div>
      </div>

      {/* Columns */}
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {displayColumns.length === 0 && (
          <div style={{ padding: '8px 12px', color: '#999', fontStyle: 'italic' }}>
            No FK columns
          </div>
        )}
        {displayColumns.map((col, idx) => (
          <Tooltip key={col.name} title={col.description || col.name} placement="right" mouseEnterDelay={0.5}>
            <div
              style={{
                padding: '4px 12px',
                borderBottom: idx < displayColumns.length - 1 ? '1px solid var(--theme-border, #f0f0f0)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'default',
              }}
            >
              {col.isPK && !col.isFK && (
                <KeyOutlined style={{ color: '#722ed1', fontSize: 11 }} />
              )}
              {col.isFK && (
                <LinkOutlined style={{ color: '#faad14', fontSize: 11 }} />
              )}
              <span style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'var(--theme-textPrimary, #333)',
                fontWeight: (col.isFK || col.isPK) ? 600 : 400,
              }}>
                {col.name}
              </span>
              <Tag
                style={{ fontSize: 10, lineHeight: '16px', margin: 0, padding: '0 4px' }}
                color={col.isFK ? 'gold' : col.isPK ? 'purple' : 'default'}
              >
                {col.type || 'TEXT'}
              </Tag>
            </div>
          </Tooltip>
        ))}
      </div>

      {/* Footer: clickable expand/collapse */}
      {(hiddenCount > 0 || isExpanded) && columns.length > keyColumns.length && (
        <div
          onClick={handleToggle}
          style={{
            padding: '5px 12px',
            fontSize: 11,
            color: '#1677ff',
            borderTop: '1px solid var(--theme-border, #f0f0f0)',
            textAlign: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
          }}
        >
          {isExpanded ? (
            <><UpOutlined style={{ fontSize: 9 }} /> Hide {nonKeyColumns.length} columns</>
          ) : (
            <><DownOutlined style={{ fontSize: 9 }} /> +{hiddenCount} more columns</>
          )}
        </div>
      )}
    </div>
  );
});

ERDTableNode.displayName = 'ERDTableNode';

export default ERDTableNode;
